/**
 * useAuthSync hook: keeps client auth state in sync with the backend session.
 * While a user is logged in (per UserContext), it re-fetches the private user
 * on mount, every 5 minutes, and on window focus via UserAPI.getUserPrivate,
 * clearing UserContext when the session has expired (a 401 surfaces as the
 * "Unauthorized" rejection from lib/axios.ts). Mounted once in App.tsx.
 */
import { useEffect, useRef } from "react";
import { useUserState, useUserDispatch, clearUserContext } from "@/contexts/UserContext";
import { UserAPI } from "@/api/user";

export const useAuthSync = () => {
    const user = useUserState();
    const dispatch = useUserDispatch();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    // need to use user.id instead of user otherwise it'll be an infinite loop since user is an object
    const userId = user?.id;

    useEffect(() => {
        const verifyAuthStatus = async () => {
            if (!userId || !dispatch) return;

            try {
                // User will update due to dispatch being passed
                await UserAPI.getUserPrivate(dispatch);
            } catch (error) {
                // If we get a 401, the user is no longer authenticated
                if (error === "Unauthorized") {
                    console.log("User session expired, clearing local state");
                    clearUserContext(dispatch);
                }
            }
        };

        // Check immediately on mount if user exists
        if (userId) {
            verifyAuthStatus();
        }

        // Set up periodic checking every 5 minutes
        intervalRef.current = setInterval(verifyAuthStatus, 5 * 60 * 1000);

        // Also check when user returns to the tab
        const handleFocus = () => {
            if (userId) {
                verifyAuthStatus();
            }
        };

        window.addEventListener("focus", handleFocus);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            window.removeEventListener("focus", handleFocus);
        };
    }, [userId, dispatch]);
};
